/**
 * Casual Competitors - 关注游戏下载 Excel 配置
 * @description 配置关注游戏模块的 Excel 列头和数据映射
 */
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { formatDecimalToPercentage } from '@intelligence/v3/utils/number';
import { buildDownloadDataNew } from '@intelligence/utils/commonUtils';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import { sortDownloadDataByConfig } from '@intelligence/components/DownloadCommon/DownloadPage/utils';

const formatList = <T extends IRecord>(list?: T[] | null): T[] => {
  if (isEmpty(list)) {
    return [];
  }
  return list!;
};

const formatMetricCellValue = (value: unknown): string | number => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : '';
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return '';
    }

    return Number.isFinite(Number(trimmedValue)) ? value : '';
  }

  return '';
};

export const getCasualCompetitorsKeyGamesConfig = (data: any, params: any, allParams: any) => {
  const granularity = params?.granularity || 'monthly';
  const isGroupByDate = allParams?.[DOWNLOAD_CFG_KEY.DATE_METHOD] !== DataCalculation.Aggregated;
  const isGroupByRegion = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;
  // 是否展示%
  const isShowRatio = allParams?.ratio_show === 1;
  // 是否显示收入指标（从 params 中获取，因为 hasRevenue 在 downloadApiParams 中传递）
  const hasRevenue = params?.hasRevenue !== false;

  const ext = data?.ext || {};
  const entityInfo = ext?.entity_info || {};
  const marketInfo = ext?.market_info || {};
  const regionNameENMap = marketInfo?.['intel_region -> en'] || {};
  const regionNameCNMap = marketInfo?.['intel_region -> cn'] || {};
  const marketNameENMap = marketInfo?.['market -> en'] || {};
  const marketNameCNMap = marketInfo?.['market -> cn'] || {};
  const regionCodeMap = marketInfo?.['market -> region'] || {};
  const pageGameIds = Array.isArray(allParams?.page_game_ids) ? allParams?.page_game_ids : [];

  const regionCountryConfig = [
    {
      title: 'Region_English',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = (item?.region || '').toLowerCase();
          return regionNameENMap?.[itemRegionCode] || itemRegionCode;
        }
        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameENMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Region_Chinese',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = (item?.region || '').toLowerCase();
          return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
        }
        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Country_English',
      key: (item: any) => {
        const itemMarketCode = (item?.market || '').toLowerCase();
        return marketNameENMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
    {
      title: 'Country_Chinese',
      key: (item: any) => {
        const itemMarketCode = (item?.market || '').toLowerCase();
        return marketNameCNMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
  ];

  // 关注游戏标签页配置
  const keyGamesSheetConfig = [
    {
      title: 'Game Name',
      key: (item: any) => {
        const gameId = item?.game_id || '';
        return entityInfo?.[gameId]?.entity_name || gameId;
      },
    },
    // Date 列调整到 Game Name 后面
    {
      title: 'Date',
      key: (item: any) => {
        const itemDate = item?.date || '';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
      when: () => isGroupByDate,
    },
    // Region/Country 列调整到 Date 后面
    ...regionCountryConfig,
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} Avg.DAU`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__daily_avg_dau),
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__daily_avg_dau_ratio),
      when: () => isShowRatio,
    },
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} Revenue($)`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__iap_revenue),
      when: () => hasRevenue,
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__iap_revenue_ratio),
      when: () => isShowRatio && hasRevenue,
    },
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} Downloads`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__download),
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__download_ratio),
      when: () => isShowRatio,
    },
    {
      title: 'Active User Share',
      key: (item: any) => formatDecimalToPercentage(item?.sensortower__dau_share),
    },
    {
      title: 'Genre',
      key: (item: any) => {
        const gameId = item?.game_id || '';
        return entityInfo?.[gameId]?.sensortower_sub_genre || '';
      },
    },
    {
      title: 'Company',
      key: (item: any) => {
        const gameId = item?.game_id || '';
        return entityInfo?.[gameId]?.publisher || '';
      },
    },
    {
      title: 'Company Country',
      key: (item: any) => {
        const gameId = item?.game_id || '';
        return entityInfo?.[gameId]?.country_name_en || entityInfo?.[gameId]?.country_abbre || '';
      },
    },
  ];

  // 排序数据：按游戏顺序 → 日期降序 → 地区排序 → 国家排序
  const regionMethod = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD];

  // 预处理数据：给每条数据添加 _region 字段（从 market 映射到 region）
  // 这样在按国家拆分时，也能按地区排序
  const listForSort = formatList(data?.list).map((item: any) => ({
    ...item,
    _region: item?.region || regionCodeMap?.[item?.market]?.toLowerCase() || '',
  }));

  const sortedList = sortDownloadDataByConfig(listForSort, {
    entityKey: 'game_id',
    entityIds: pageGameIds,
    isGroupByDate,
    regionMethod,
    // 使用 _region 字段作为地区排序依据
    regionKey: '_region',
    // 使用 market 字段作为国家排序依据
    marketKey: 'market',
  });

  return {
    keyGames: {
      sheetName: '关注游戏',
      style: 'normal',
      data: buildDownloadDataNew(keyGamesSheetConfig, sortedList),
    },
    summary: {
      sheetName: 'Summary',
      style: 'summary',
      data: [
        ['Platform', Array.isArray(params?.platforms) ? params?.platforms?.join(', ') : ''],
        ['Regions/Countries', Array.isArray(params?.markets) ? params?.markets?.join(', ') : ''],
        ['Date Start', params?.start_date || ''],
        ['Date End', params?.end_date || ''],
      ],
    },
  };
};
